import { useMemo } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/pages/home/components/Navbar";
import Footer from "@/pages/home/components/Footer";
import ProductCard from "@/pages/home/components/ProductCard";
import { useShop } from "@/store/ShopContext";
import { useProductsByIds, useVariantsByProductIds } from "@/hooks/useProducts";

export default function FavoritesPage() {
  const { favorites, toggleFavorite, showToast } = useShop();
  const { products, loading, error } = useProductsByIds(favorites);

  const ordered = [...products].sort(
    (a, b) => favorites.indexOf(b.id) - favorites.indexOf(a.id),
  );

  const orderedIds = useMemo(() => ordered.map((p) => p.id), [ordered]);
  const { variantsByProduct } = useVariantsByProductIds(orderedIds);

  const onClearAll = () => {
    if (favorites.length === 0) return;
    favorites.forEach((id) => toggleFavorite(id));
    showToast("Favoritos removidos");
  };

  return (
    <div className="min-h-screen bg-background-50 text-foreground-900 flex flex-col">
      <Navbar />

      <main className="flex-1">
        <section className="max-w-[1280px] mx-auto px-4 md:px-8 pt-8 md:pt-10 pb-6">
          <nav aria-label="Breadcrumb" className="text-[13px] text-foreground-600 mb-4">
            <ol className="flex items-center gap-1.5">
              <li>
                <Link to="/" className="hover:underline cursor-pointer">
                  Início
                </Link>
              </li>
              <li><i className="ri-arrow-right-s-line text-foreground-400"></i></li>
              <li className="text-foreground-900">Favoritos</li>
            </ol>
          </nav>

          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div>
              <h1 className="font-heading text-foreground-950 text-3xl md:text-4xl">
                Favoritos
              </h1>
              {favorites.length > 0 && (
                <p className="text-[14px] text-foreground-700 mt-2">
                  {favorites.length} {favorites.length === 1 ? "item" : "itens"} salvos
                </p>
              )}
            </div>
            {favorites.length > 0 && (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={onClearAll}
                  className="px-4 h-10 inline-flex items-center rounded-full border border-background-300 hover:border-foreground-900 text-foreground-800 text-[13px] font-medium transition-colors cursor-pointer whitespace-nowrap"
                >
                  Remover todos
                </button>
              </div>
            )}
          </div>
        </section>

        <section className="max-w-[1280px] mx-auto px-4 md:px-8 pb-16">
          {loading && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-5 gap-y-8 animate-pulse">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i}>
                  <div className="aspect-square rounded-md bg-background-200/60"></div>
                  <div className="h-4 w-2/3 mt-3 bg-background-200/60 rounded"></div>
                  <div className="h-4 w-1/3 mt-2 bg-background-200/60 rounded"></div>
                </div>
              ))}
            </div>
          )}

          {!loading && error && (
            <div className="py-16 text-center">
              <p className="text-foreground-800 mb-3">{error}</p>
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="px-5 py-2.5 rounded-full bg-foreground-950 hover:bg-foreground-800 text-background-50 text-sm font-medium transition-colors cursor-pointer"
              >
                Tentar novamente
              </button>
            </div>
          )}

          {!loading && !error && favorites.length === 0 && (
            <div className="py-16 md:py-20 text-center max-w-md mx-auto">
              <div className="w-20 h-20 rounded-full bg-primary-100 flex items-center justify-center mx-auto mb-5">
                <i className="ri-heart-line text-primary-600 text-[32px]"></i>
              </div>
              <h2 className="font-heading text-foreground-950 text-2xl mb-4">
                Nenhum favorito ainda
              </h2>
              <p className="text-[14px] text-foreground-700 mb-6">
                Clique no coração de qualquer produto para salvar aqui.
              </p>
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-foreground-950 hover:bg-foreground-800 text-background-50 text-[14px] font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-left-line"></i>
                Explorar produtos
              </Link>
            </div>
          )}

          {!loading && !error && favorites.length > 0 && ordered.length === 0 && (
            <div className="py-16 text-center text-foreground-700">
              Produto indisponível.
            </div>
          )}

          {!loading && !error && ordered.length > 0 && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-5 gap-y-10">
              {ordered.map((p) => (
                <ProductCard
                  key={p.id}
                  product={p}
                  variants={variantsByProduct[p.id] ?? []}
                />
              ))}
            </div>
          )}
        </section>
      </main>

      <Footer />
    </div>
  );
}